import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { createReference } from "@medplum/core";
import { Observation, Practitioner, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";

import {
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_POSTPARTUM_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { LaborQuestionnaireDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/LaborQuestionnaireDrawer";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import { useResolveLaborAmendEncounterParentVersionIdForStamp } from "@/encounters/components/EncounterLabor/hooks/use-labor-amend-encounter-parent-version-id";
import { useLaborQuestionnaireForm } from "@/encounters/components/EncounterLabor/hooks/use-labor-questionnaire-form";
import { useRefreshTrigger } from "@/encounters/components/EncounterLabor/hooks/use-refresh-trigger";
import {
  addQuestionnaireResponseVersioning,
  getQuestionnaire,
  getQuestionnaireResponseFromObservation,
} from "@/encounters/components/EncounterLabor/services/labor";
import { extractDateTimeFromFormValues } from "@/encounters/components/EncounterLabor/utils/form-datetime";
import { getPostpartumFlowsheetEditReplaceKey } from "@/encounters/components/EncounterLabor/utils/labor-amend-pending-keys";
import { formatNormalizedFormValuesForAmendDisplay } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { buildLaborQuestionnaireSubmitValues } from "@/encounters/components/EncounterLabor/utils/labor-questionnaire-submit-values";
import { applyQuestionnaireResponseEncounterParentVersionId } from "@/encounters/utils/questionnaire-response-amend-stamp";
import { markQuestionnaireFormAsEditing } from "@/intake-forms/types/questionnaire-form";
import { selectBirthDateTime } from "@/store/slices/birth";
import { formatBirthDateTimeForDisplay, isDateTimeOnOrAfterBirth } from "@/utils/datetime";
import { joinReferenceString } from "@/utils/medplum";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

import {
  CLIENT_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP,
  NEWBORN_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP,
} from "../../constants/postpartum-flowsheets";

interface PostpartumFlowsheetEditRowProps {
  observation?: Observation;
  onClose?: () => void;
  onRefresh?: () => Promise<void>;
  flowsheetId?: string;
}

export default function PostpartumFlowsheetEditRow({
  observation,
  onClose,
  onRefresh,
  flowsheetId,
}: PostpartumFlowsheetEditRowProps) {
  const [opened, setOpened] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialQuestionnaireResponse, setInitialQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >();
  const { encounterId, clientId } = useParams();
  const medplum = useMedplum();
  const practitioner = useMedplumProfile() as Practitioner;
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const resolveEncounterQuestionnaireParentVersionIdForStamp =
    useResolveLaborAmendEncounterParentVersionIdForStamp();
  const birthDateTime = useSelector(selectBirthDateTime);
  const { refreshTrigger } = useRefreshTrigger();

  const questionnaireDisplayName = observation?.code?.coding?.[0]?.display;

  function normalize(str: string) {
    return str.trim().toLowerCase();
  }

  const isClientQuestionnaire =
    (observation?.code?.coding?.[0]?.code?.includes("clientPostpartumFlowSheet") ||
      observation?.code?.coding?.[0]?.code?.includes("laborFlowSheet")) ??
    false;

  const questionnaireMap = isClientQuestionnaire
    ? CLIENT_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP
    : NEWBORN_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP;

  const normalizedMap = Object.fromEntries(
    Object.entries(questionnaireMap).map(([k, v]) => [normalize(k), v as string]),
  ) as Record<string, string>;

  const normalizedDisplayName = questionnaireDisplayName ? normalize(questionnaireDisplayName) : "";
  const questionnaireName = normalizedDisplayName
    ? normalizedMap[normalizedDisplayName] || null
    : null;

  // Fetch the questionnaire response when the component mounts or observation changes
  useEffect(() => {
    const fetchData = async () => {
      if (observation) {
        const response = await getQuestionnaireResponseFromObservation(medplum, observation);
        setInitialQuestionnaireResponse(response);
      }
    };
    fetchData();
  }, [medplum, observation]);

  const {
    isLoading,
    questionnaire,
    questionnaireResponse,
    formSchema,
    form,
    questionnaireError,
    dateTimeUtcDateAnchors,
  } = useLaborQuestionnaireForm({
    key: refreshTrigger, // Use refreshTrigger as key to force re-mount
    questionnaireName,
    encounterId,
    questionnaireId: undefined, // We'll update this after we have the questionnaire
    questionnaireResponse: initialQuestionnaireResponse,
    editMode: true,
    refreshTrigger, // Pass refresh trigger to force re-fetch
  });

  // Mark the form as editing immediately (synchronous) so child fields can read it on first render
  markQuestionnaireFormAsEditing(form);

  const handleClose = () => {
    setOpened(false);
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    // Cumulative blood loss observations are now handled by bot processing

    if (
      !questionnaire?.id ||
      !formSchema ||
      !practitioner ||
      !encounterId ||
      !clientId ||
      !questionnaireName
    ) {
      handleClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedValues = buildLaborQuestionnaireSubmitValues({
        form,
        formSchema,
        questionnaire,
        dateTimeUtcDateAnchors,
      });
      if (!normalizedValues) {
        return;
      }

      // Validate date/time is on or after birth for postpartum forms
      // Only validate if birthDateTime exists AND form has date/time fields
      if (birthDateTime) {
        const { date, time } = extractDateTimeFromFormValues(normalizedValues, questionnaireName);
        // Only validate if both date and time are present
        if (date && time && !isDateTimeOnOrAfterBirth(date, time, birthDateTime)) {
          const birthDisplayTime = formatBirthDateTimeForDisplay(birthDateTime);
          notifications.show({
            title: "Invalid Date/Time",
            message: `The date and time must be on or after the birth time (${birthDisplayTime}). Postpartum entries cannot be recorded before birth.`,
            color: "red",
            autoClose: 8000,
          });
          Sentry.captureException(
            new Error(
              `Invalid date/time: birth time (${birthDisplayTime}), form date time (${date} ${time}). Postpartum entries cannot be recorded before birth.`,
            ),
          );
          console.warn(
            `Invalid date/time: birth time (${birthDisplayTime}), form date time (${date} ${time}). Postpartum entries cannot be recorded before birth.`,
          );
          return;
        }
      }

      const performPersist = async () => {
        const questionnaireResource = await getQuestionnaire(medplum, questionnaireName);
        if (!questionnaireResource) {
          console.error(`Questionnaire ${questionnaireName} not found`);
          throw new Error(`Questionnaire ${questionnaireName} not found`);
        }

        const questionnaireResponseToUpdate =
          (observation
            ? await getQuestionnaireResponseFromObservation(medplum, observation)
            : undefined) ?? initialQuestionnaireResponse;

        if (!questionnaireResponseToUpdate) {
          throw new Error("No existing questionnaire response found to update");
        }

        const authorReference = practitioner.id
          ? joinReferenceString("Practitioner", practitioner.id)
          : undefined;
        const patientReference = { reference: joinReferenceString("Patient", clientId) };
        const practitionerReference = createReference(practitioner);

        const versionedQuestionnaireResponse = observation
          ? addQuestionnaireResponseVersioning(
              {
                ...questionnaireResponseToUpdate,
                item: getQuestionnaireResponseFromFormValues(
                  questionnaireResource,
                  normalizedValues,
                  patientReference,
                  practitionerReference,
                ).item,
                authored: new Date().toISOString(),
              },
              questionnaireResponseToUpdate,
              observation,
              authorReference,
            )
          : {
              ...questionnaireResponseToUpdate,
              item: getQuestionnaireResponseFromFormValues(
                questionnaireResource,
                normalizedValues,
                patientReference,
                practitionerReference,
              ).item,
              authored: new Date().toISOString(),
            };

        const stampedQuestionnaireResponse = applyQuestionnaireResponseEncounterParentVersionId(
          versionedQuestionnaireResponse,
          resolveEncounterQuestionnaireParentVersionIdForStamp(),
        );

        await medplum.updateResource(stampedQuestionnaireResponse);

        if (onRefresh) {
          await onRefresh();
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      };

      const replaceKey = observation?.id
        ? getPostpartumFlowsheetEditReplaceKey(observation.id)
        : undefined;
      const wasAmendPersistEnqueued = enqueueAmendPersist({
        replaceKey,
        pendingDisplayEntry: observation?.id
          ? {
              key: replaceKey!,
              title: questionnaireDisplayName ?? "Postpartum flowsheet",
              details: formatNormalizedFormValuesForAmendDisplay(
                normalizedValues as Record<string, unknown>,
              ),
            }
          : undefined,
        persist: performPersist,
        queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
        errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_POSTPARTUM_MESSAGE,
        onQueued: handleClose,
      });
      if (wasAmendPersistEnqueued) {
        return;
      }

      await performPersist();
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LaborQuestionnaireDrawer
      form={form}
      opened={opened}
      onClose={handleClose}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      submitButtonText="Update"
      error={questionnaireError}
      isSubmitting={isSubmitting}
      questionnaire={questionnaire}
      title={questionnaireDisplayName || ""}
      questionnaireResponse={questionnaireResponse}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${questionnaireName}`}
    />
  );
}
